import { Component, NgZone } from '@angular/core';
import { PushNotifications, Token, ActionPerformed, PushNotificationSchema } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { ConfigService } from 'src/app/provider/config.service';
import { AuthService } from 'src/app/services/auth.service';
import { RouterStateTrackerService } from 'src/app/services/router-state-tracker.service';
import { first } from 'rxjs/operators';
import { NativeBiometric, AvailableResult } from 'capacitor-native-biometric';
import { environment } from 'src/environments/environment';
import { endpoints } from 'src/app/constant/endpoints';
import { ApiService } from 'src/app/provider/api.service';
import { DataService } from 'src/app/services/data.service';
import { AlertController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'signin',
  templateUrl: './signin.component.html',
  styleUrls: ['./signin.component.scss'],
  standalone: false,
})
export class SignInComponent {
  loginForm = {
    phone: '',
    password: '',
    device_token: '',
  };

  // UI state
  showPasswordFlag = false;
  passwordInputType: 'password' | 'text' = 'password';
  dialCode = '965'; // example default
  userPhoneAllowedDigit = 8;
  loginState = '';
  cardOpacity = 1;

  // external values
  pushToken: string = ''; // inject actual push token logic
  storeData?: any;
  country_iso_code?: any;

  loading: boolean = false;
  constructor(
    private apiService: ApiService,
    private authService: AuthService,
    private configService: ConfigService,
    private dataService: DataService,
    private ngZone: NgZone,
    private alertCtrl: AlertController,
    private translate: TranslateService,
    private routerStateTrackerService: RouterStateTrackerService,

  ) {
    // Setup push notifications for Capacitor
    this.setupPushNotifications();
        if (environment.IS_INTERNAL == true) {
          this.loginForm.phone = '60637521';
          this.loginForm.password = '009009009dm';
        } else {
          this.loginForm.phone = '';
          this.loginForm.password = '';
        }
            const config = this.dataService.getCachedData('configurations');
      
      this.storeData = config?.organization;
        this.userPhoneAllowedDigit = this.storeData?.allowed_digit_for_phone || 10;
        this.country_iso_code = this.storeData?.dialing_code || '965';
        
  }

  // --- Input handling
  onPhoneInput(event: Event) {
    const input = (event.target as HTMLInputElement).value.replace(/\D/g, '');
    this.loginForm.phone = input.slice(0, this.userPhoneAllowedDigit);
  }

  togglePasswordVisibility(): void {
    this.showPasswordFlag = !this.showPasswordFlag;
    this.passwordInputType = this.showPasswordFlag ? 'text' : 'password';
  }
  setupPushNotifications(): void {
    // Only setup push notifications if running on native platform
    if (!Capacitor.isNativePlatform()) {

      return;
    }

    if (!Capacitor.isPluginAvailable('PushNotifications')) {

      return;
    }

    PushNotifications.requestPermissions().then((result: any) => {
      if (result.receive === 'granted') {
        PushNotifications.register();
        // this.getFCMToken();
      }
    });

    PushNotifications.addListener('registration', (token: Token) => {
      this.pushToken = token.value;
    });

    PushNotifications.addListener('registrationError', (error: any) => {
      console.error('Push registration error:', error);
    });

    PushNotifications.addListener('pushNotificationReceived', (_notification: PushNotificationSchema) => {});

    PushNotifications.addListener('pushNotificationActionPerformed', (_notification: ActionPerformed) => {});
  }
  // --- Biometric
  async manualBiometricLogin(): Promise<void> {
    
    try {
      this.ngZone.run(() => {
        this.loading = true;
  
      });


      const loginData = await this.configService.getStorage('loginData');

      
      if (loginData) {
  
        await this.performBiometricVerification(loginData);
      } else {
  
        this.ngZone.run(() => {
          this.loading = false;
          this.toastMessage('no_login_data_found_for_biometric');
        });
      }
    } catch (error) {
      console.error('❌ Error in manualBiometricLogin:', error);
      this.ngZone.run(() => {
        this.loading = false;
        this.toastMessage('error_accessing_biometric_data');
      });
    }
  }
  
  
  async showAlert(title: any, msg: any) {
    await this.alertCtrl?.create({
      header: this.translate?.instant(title),
      message: this.translate?.instant(msg),
      buttons: [this.translate?.instant('ok')],
    });
  };

  private async performBiometricVerification(loginData: any): Promise<void> {
    
    try {
      this.loading = true;

      
      const result: AvailableResult = await NativeBiometric.isAvailable();

      
      if (!result.isAvailable) {
  
        this.ngZone.run(() => {
          this.loading = false;
          this.toastMessage('biometric_not_available');
        });
        return;
      }


      
      // Perform biometric verification
      const verifyResult = await NativeBiometric.verifyIdentity({
        reason: `Login to ${this.configService.currentSettings.appName}`,
        title: `Login to ${this.configService.currentSettings.appName}`,
        subtitle: 'Verify identity',
        description: `Unlock your device to use ${this.configService.currentSettings.appName}`,
      });
      


      // Success - run in NgZone to ensure Angular detects changes
      this.ngZone.run(async () => {
  
        this.loading = false;
        this.cardOpacity = 0;
        this.loginForm.phone = loginData.phone;
        this.loginForm.password = loginData.password;
  
        await this.onSubmitLogin();
      });

    } catch (error: any) {
      console.error('❌ Biometric verification error:', error);
      console.error('❌ Error code:', error.code);
      console.error('❌ Error message:', error.message);
      console.error('❌ Full error object:', JSON.stringify(error));
      
      // Run in NgZone to ensure UI updates
      this.ngZone.run(() => {
        this.loading = false;
        this.cardOpacity = 1;
        
        // Check if user cancelled
        if (error.code === 10 || error.message?.includes('cancel') || error.message?.includes('Cancel')) {
    
          this.toastMessage('biometric_cancelled');
        } else {
    
          this.toastMessage('biometric_verification_failed');
        }
      });
    }
  }

  // --- Login
async onSubmitLogin(): Promise<void> {
  if (!this.loginForm.phone || !this.loginForm.password) {
    this.toastMessage('phone_and_password_required');
    return;
  }
// await this.showAlert("Failed", 'login_failed');
  this.loading = true;
  this.loginForm.device_token = this.pushToken;

  try {
    const response = await this.apiService
      ?.post(endpoints.login, this.loginForm)
      .pipe(first())
      .toPromise();

    if (response?.success) {
      this.cardOpacity = 1;

      const storageLoginData = {
        phone: Number(this.loginForm.phone),
        password: this.loginForm.password,
      };

      const customer = response.data?.customer;

      // Save auth data (customer object from API contains is_verified)
      await this.authService.setAuthToken(customer?.session_token);
      await this.authService.setAuthUser(customer);

      // Check verification status from API response
      if (customer?.is_verified === false) {
        // User not verified → navigate to OTP
        this.loading = false;
        const navigationPayload: Record<string, unknown> = {
          phone: this.loginForm.phone,
          email: '',
          country_iso_code: this.country_iso_code,
          otp_sent: false,
          context: 'signin',
          redirectTo: 'calendar',
          historyRoute: environment.LOGIN_PAGE,
          loginData: storageLoginData,
        };

        this.navigateToPage('otp-verification', navigationPayload);
      } else {
        // User is verified → proceed to app
        await this.configService.setStorage('loginData', storageLoginData);
        await this.updateAppSettingsWithUser();

        this.loading = false;
        this.navigateToPage('/calendar');
      }
    } else {
      this.loading = false;
      this.toastMessage(response?.message || 'login_failed');
    }
  } catch (err: any) {
    console.error('Login error:', err);
    this.loading = false;
    const errorMessage =
      err.error?.message || err.error?.error || 'an_unexpected_error_occurred';
    // this.toastMessage(errorMessage);
     this.toastMessage(errorMessage || 'login_failed');
  }
}

  async updateAppSettingsWithUser(): Promise<void> {
    const authenticatedUser = this.authService.getAuthUser();
    await this.configService.updateAppSettings({
      currentUser: authenticatedUser || null,
    });

  }

  async resetPassword(): Promise<void> {
    const phoneNumber = this.loginForm.phone;
    if (!phoneNumber) {
      this.toastMessage('phone_number_is_required');
      return;
    }
this.loading = true;
    try {
      const response = await this.apiService?.post(endpoints.resetPassword, { phone: phoneNumber }).pipe(first()).toPromise();

      if (response?.success) {
        this.loading = false;
        this.toastMessage(response.message);
        this.ngZone.run(() => {
          this.navigateToPage('/reset-password', {
            phoneNo: phoneNumber,
            country_iso_code: this.country_iso_code,
            redirectTo: environment.LOGIN_PAGE,
            historyRoute: environment.LOGIN_PAGE,
          });
        });
      } else {
        this.loading = false;
        this.toastMessage(response?.message || 'password_reset_failed');
      }
    } catch (err: any) {
      console.error('Password reset error:', err);
      this.loading = false;
      this.toastMessage(err.error?.message || 'an_unexpected_error_occurred');
    }
  }

  // --- Utils
  // private navigateToPage(path: string, extras?: any): void {
  //   this.router?.navigate([path], { queryParams: extras || {} });
  // }
  navigateToPage(page: string, data: any = { test: 'From Onb' }): void {
    this.routerStateTrackerService.navigateWithPreExitAnimation(page, data);
  }
  private async toastMessage(message: string): Promise<void> {
    const alert = await this.alertCtrl.create({
      header: 'Alert',
      message: this.translate?.instant(message),
      buttons: ['OK']
    });
    await alert.present();
  }
}
